import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import {
  ArrowRightIcon,
  BellIcon,
  CalendarDotsIcon,
  CheckCircleIcon,
  HandshakeIcon,
  type Icon as PhosphorIcon,
  MagnifyingGlassIcon,
  SquaresFourIcon,
} from '@phosphor-icons/react';
import { isReportActionKey } from '@logspot-platform/platform-types';
import type { ActionCatalogEntry } from '@/api/ai-service';
import { actionCatalogOptions, actionsOptions } from '@/hooks/query-options';
import { hasPermission } from '@/lib/permissions';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { Input } from '@/components/ui/input';
import { useFeatures } from '@/hooks/use-features';
import { ConfigureActionDialog } from '@/components/actions/configure-action-dialog';
import { ActionDetailDialog } from '@/components/actions/action-detail-dialog';
import { RunActionFormDialog } from '@/components/actions/run-forms';
import {
  RUN_MODE_ICONS,
  RUN_MODE_LABELS,
} from '@/components/actions/run-mode-labels';
import { ActionCard } from '@/components/actions/action-card';
import { CategoryChip } from '@/components/category-chip';
import { actionVisual } from '@/components/actions/action-visuals';
import { ActionAgentsMeta } from '@/components/actions/action-agents';

export const Route = createFileRoute('/_auth/_side-layout/actions/catalog')({
  component: CatalogPage,
  beforeLoad: async ({ context }) => {
    // Match the Actions nav guard (category-menu gates on `action:create`), so a
    // role that sees the nav link can't be redirected off the catalog route.
    if (!hasPermission(context.userRole, { action: ['create'] })) {
      throw redirect({ to: '/' });
    }
  },
  loader: ({ context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(actionCatalogOptions()),
      queryClient.ensureQueryData(actionsOptions()),
    ]),
});

// Catalog categories name what an action is for, not whether an agent runs it
// (agents also write digests): Lookups (instant), Sales (work one lead),
// Digests (recurring summaries), Notifications (event-triggered delivery).
type Category = 'lookup' | 'sales' | 'digest' | 'notification';
const categoryOf = (action: ActionCatalogEntry): Category =>
  isReportActionKey(action.key)
    ? 'digest'
    : action.kind === 'lookup'
      ? 'lookup'
      : action.kind === 'template'
        ? 'notification'
        : 'sales';

const CATEGORY_META: Record<
  Category,
  { label: string; icon: PhosphorIcon; tag: string }
> = {
  lookup: {
    label: 'Lookups',
    icon: MagnifyingGlassIcon,
    tag: 'Instant · no agent',
  },
  sales: {
    label: 'Sales',
    icon: HandshakeIcon,
    tag: 'Qualify, research and reach a lead',
  },
  digest: {
    label: 'Digests',
    icon: CalendarDotsIcon,
    tag: 'Recurring summaries',
  },
  notification: {
    label: 'Notifications',
    icon: BellIcon,
    tag: 'Event-triggered delivery',
  },
};
// 'lookup' stays in the type/meta for future instant actions, but the lookups
// themselves live in the enrichment surfaces and are hidden from this catalog,
// so the chip/group isn't offered.
const CATEGORY_ORDER: Category[] = ['sales', 'digest', 'notification'];

function CatalogCard({
  action,
  added,
  onOpen,
}: {
  action: ActionCatalogEntry;
  added: boolean;
  onOpen: (action: ActionCatalogEntry) => void;
}) {
  const { Icon, accent } = actionVisual(action.key, action.kind);
  return (
    <ActionCard
      accent={accent}
      icon={<Icon />}
      title={action.label}
      onClick={() => onOpen(action)}
      // Clean click-to-view: the whole card opens the detail; hovering (or
      // focusing) reveals the View affordance instead of fake buttons/badges.
      actions={
        <span className="text-primary pointer-events-none flex items-center gap-1 text-sm font-medium opacity-0 transition-opacity group-hover/action-card:opacity-100 group-focus-visible/action-card:opacity-100">
          View
          <ArrowRightIcon className="size-3.5" />
        </span>
      }
      description={action.description ? <p>{action.description}</p> : undefined}
      footer={
        <div className="space-y-2">
          <ActionAgentsMeta agents={action.agents} />
          {added ? (
            <Link
              to="/actions/your-actions"
              onClick={(e) => e.stopPropagation()}
              className="text-primary flex items-center gap-1.5 text-sm font-medium hover:underline"
            >
              <CheckCircleIcon weight="fill" className="size-4" />
              Added · View in Your Actions
            </Link>
          ) : (
            // Run modes as quiet metadata (same treatment as the agent line).
            <div className="text-muted-foreground flex flex-wrap items-center gap-2.5 text-sm">
              {action.run_modes.map((mode, index) => {
                const ModeIcon = RUN_MODE_ICONS[mode];
                return (
                  <span key={mode} className="flex items-center gap-2.5">
                    {index > 0 && <span className="bg-border h-3 w-px" />}
                    <span className="inline-flex items-center gap-1.5 [&_svg]:size-3.5">
                      {ModeIcon && <ModeIcon />}
                      {RUN_MODE_LABELS[mode] ?? mode}
                    </span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      }
    />
  );
}

// Browse Catalog: every billable action, grouped by category with live filter
// chips + search. A card opens the detail dialog (what it does + its agent),
// from which you Add to Library or Set Up. Web-search-only actions are hidden
// while no provider is configured. Super-admins get NO special visibility —
// they see exactly what the org's resolved flags allow (flip the org's
// feature overrides to QA a gated action through the real customer path).
function ActionCatalog() {
  const { data, isLoading } = useQuery(actionCatalogOptions());
  const { data: configured = [] } = useQuery(actionsOptions());
  // Launch gates: hide an action when NONE of its run modes can execute yet,
  // the same honesty rule as the Set Up dialog's "Coming soon".
  const { isEnabled } = useFeatures();
  const modeEnabled = (mode: string) =>
    mode === 'once' ||
    mode === 'trigger' ||
    (mode === 'schedule' && isEnabled('actionsScheduler'));

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | Category>('all');
  const [detailFor, setDetailFor] = useState<ActionCatalogEntry | null>(null);
  const [setUpFor, setSetUpFor] = useState<ActionCatalogEntry | null>(null);
  // A form-driven action's "Save & Run" hands off here — the per-run input
  // dialog outlives the configure dialog it was launched from.
  const [runFormFor, setRunFormFor] = useState<ActionCatalogEntry | null>(null);

  const actions = data?.actions ?? [];
  const webSearchAvailable = data?.web_search_available ?? false;
  const addedKeys = useMemo(
    () => new Set(configured.map((c) => c.action_key)),
    [configured],
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return actions.filter((action) => {
      // Entity-maintenance verbs (profile.build, account.watch) live on their
      // entity surfaces — never browsable here, but still in the response so
      // Your Actions rows and the configure dialog resolve their entry.
      if (action.hidden_from_catalog) return false;
      // Availability gates (dark-launch honesty) hide actions you could newly
      // add but can't use yet. They apply to everyone — super-admins QA by
      // enabling the org's flag, not through a visibility side door. Anything
      // ALREADY in Your Actions stays browsable: hiding an owned action reads
      // as data loss, and its run gate still enforces the flags.
      if (!addedKeys.has(action.key)) {
        if (action.feature_key && !isEnabled(action.feature_key)) {
          return false;
        }
        if (action.requires_web_search && !webSearchAvailable) {
          return false;
        }
        if (!action.run_modes.some(modeEnabled)) return false;
      }
      if (filter !== 'all' && categoryOf(action) !== filter) return false;
      if (!term) return true;
      return (
        action.label.toLowerCase().includes(term) ||
        (action.description?.toLowerCase().includes(term) ?? false) ||
        action.agents.some((agent) => agent.label.toLowerCase().includes(term))
      );
    });
  }, [actions, search, filter, webSearchAvailable, isEnabled, addedKeys]);

  const grouped = useMemo(
    () =>
      CATEGORY_ORDER.map((cat) => ({
        cat,
        items: visible.filter((a) => categoryOf(a) === cat),
      })).filter((g) => g.items.length > 0),
    [visible],
  );

  if (isLoading) {
    return <p className="text-muted-foreground text-sm">Loading…</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search actions…"
          className="w-full sm:w-72"
        />
        <CategoryChip
          label="All"
          icon={SquaresFourIcon}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        {CATEGORY_ORDER.map((cat) => (
          <CategoryChip
            key={cat}
            label={CATEGORY_META[cat].label}
            icon={CATEGORY_META[cat].icon}
            active={filter === cat}
            onClick={() => setFilter(cat)}
          />
        ))}
      </div>

      {grouped.length === 0 ? (
        <p className="text-muted-foreground text-sm">No actions match.</p>
      ) : (
        <div className="space-y-8">
          {grouped.map(({ cat, items }) => {
            const meta = CATEGORY_META[cat];
            return (
              <section key={cat} className="space-y-3">
                <div className="text-muted-foreground flex items-center gap-3 text-xs font-semibold tracking-wide uppercase">
                  <meta.icon className="text-primary size-3.5" />
                  {meta.label}
                  <span className="border-border h-px flex-1 border-t" />
                  <span className="text-muted-foreground/70 normal-case">
                    {meta.tag}
                  </span>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {items.map((action) => (
                    <CatalogCard
                      key={action.key}
                      action={action}
                      added={addedKeys.has(action.key)}
                      onOpen={setDetailFor}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <ActionDetailDialog
        action={detailFor}
        added={detailFor !== null && addedKeys.has(detailFor.key)}
        open={detailFor !== null}
        onOpenChange={(open) => !open && setDetailFor(null)}
        onSetUp={(action) => {
          setDetailFor(null);
          setSetUpFor(action);
        }}
      />

      {setUpFor && (
        <ConfigureActionDialog
          open={setUpFor !== null}
          onOpenChange={(open) => !open && setSetUpFor(null)}
          actionKey={setUpFor.key}
          onRunForm={() => setRunFormFor(setUpFor)}
        />
      )}

      <RunActionFormDialog
        actionKey={runFormFor?.key}
        actionName={runFormFor?.label}
        open={runFormFor !== null}
        onOpenChange={(open) => !open && setRunFormFor(null)}
      />
    </div>
  );
}

// Browse Catalog: the "what can I run" counterpart to the Integrations
// directory's "what can I connect".
function CatalogPage() {
  return (
    <>
      <PageHeader
        title="Browse Catalog"
        description="Browse actions and set them up to run manually, on a schedule, or on an event."
        isNew
      />
      <PageContent>
        <ActionCatalog />
      </PageContent>
    </>
  );
}
